import { isEmpty } from 'lodash'

export const TAG_REGEX = /[^0-9a-z-_]+/

export const STATUS_ERROR = 'error'
export const STATUS_WARNING = 'warning'
export const STATUS_CLEAN = 'clean'

// User-level permission column → org-level feature flag that gates it. Mirrors
// the backend FEATURE_GATED_PERMISSIONS in core-service bulk_upload.py. Keep
// the two lists in sync — the backend is the source of truth, but matching
// here means users see the error in the validation column instead of waiting
// for the server to reject the row.
export const FEATURE_GATED_PERMISSIONS = {
  edit_qa: { flag: 'qa_enabled', label: 'QA Access' },
  edit_qa_copilot: { flag: 'qa_copilot_enabled', label: 'Quality Access' },
  realtime_coaching_access: {
    flag: 'realtime_coaching_access',
    label: 'Real-Time Coaching Access',
  },
  desktop_enterprise_enabled: {
    flag: 'desktop_enterprise_enabled',
    label: 'Balto Enterprise',
  },
  agent_gpt_access: { flag: 'agent_gpt_enabled', label: 'Agent GPT Access' },
  tools_editor: { flag: 'tools_enabled', label: 'Tools Editor' },
  newrez_complaints_enabled: {
    flag: 'newrez_complaints_enabled',
    label: 'Complaints',
  },
  outbound_campaign_editor: {
    flag: 'outbound_campaigns_enabled',
    label: 'Outbound Campaign Editor',
  },
  togo_editor: {
    flag: 'togo_enabled',
    label: 'Togo Editor',
  },
  // RTG3-878. Unlike the others this one has a transition guard in the backend
  // (see the forEach below) — re-asserting a value the user already holds is
  // not a grant.
  screen_capture_enabled: {
    flag: 'screen_capture_access_enabled',
    label: 'Screen Capture Access',
  },
}

// Legacy alias → canonical permission. The backend reads both column names
// (`csv_user.get("newrez_complaints_enabled") or csv_user.get("complaints_enabled")`),
// so validation must mirror that or it will mark rows valid that the
// server will then reject.
const PERMISSION_COLUMN_ALIASES = {
  complaints_enabled: 'newrez_complaints_enabled',
}

// summary-service can't submit a complaint to NewRez without the agent's
// voip_user_id, so granting complaints without one is a misconfiguration.
export const COMPLAINTS_REQUIRES_VOIP_USER_ID_ERROR =
  'voip_user_id is required when complaints are enabled'

// A CSV cell counts as a grant when it parses to True via the backend's
// determine_access_value (yes / true / 1) — or for realtime_coaching_access,
// "standard" / "admin". Anything else (no / false / 0 / null / empty / "") is
// either a revoke or a no-op, neither of which we want to block.
const isGrant = (rawValue, permission) => {
  if (rawValue == null) {
    return false
  }
  const normalized = String(rawValue).trim().toLowerCase()
  if (permission === 'realtime_coaching_access') {
    return normalized === 'standard' || normalized === 'admin'
  }
  return ['yes', 'true', '1'].includes(normalized)
}

export const buildValidationContext = ({
  organizationTags,
  playbooks,
  tagCategories,
  existingUsers,
  users,
  organizationFeatureFlags = {},
}) => {
  const existingTagNames = new Set(organizationTags.map((tag) => tag.name))
  const existingPlaybookNames = new Set(playbooks.map((playbook) => playbook.name))
  const tagCategoryNames = new Set(tagCategories.map((category) => category.name))

  const existingUserByName = new Map(
    existingUsers
      .filter((existingUser) => existingUser.username)
      .map((existingUser) => [existingUser.username.toLowerCase(), existingUser])
  )

  const usernameCounts = new Map()
  users.forEach((user) => {
    const username = user.username?.toLowerCase()
    if (!username) {
      return
    }
    usernameCounts.set(username, (usernameCounts.get(username) || 0) + 1)
  })
  const duplicateUsernames = new Set(
    [...usernameCounts.entries()].filter(([, count]) => count > 1).map(([username]) => username)
  )

  return {
    existingTagNames,
    existingPlaybookNames,
    existingUserByName,
    tagCategoryNames,
    duplicateUsernames,
    organizationFeatureFlags,
  }
}

const collectRowTags = (row, tagCategoryNames) => {
  const collected = []
  if (row.uncategorized_tags) {
    collected.push(
      ...row.uncategorized_tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    )
  }
  Object.entries(row).forEach(([key, value]) => {
    if (tagCategoryNames.has(key) && value && value.trim()) {
      collected.push(value.trim())
    }
  })
  return collected
}

export const validateRow = (row, context) => {
  const errors = []
  const warnings = []

  const username = row.username?.toLowerCase()

  if (!username) {
    errors.push('Username is required')
  } else if (context.duplicateUsernames.has(username)) {
    errors.push('Duplicate username in file')
  }

  const existingUser = username ? context.existingUserByName.get(username) : null
  const isExisting = Boolean(existingUser)

  if (row.playbook) {
    if (!context.existingPlaybookNames.has(row.playbook)) {
      errors.push(`Unknown playbook: ${row.playbook}`)
    }
  } else if (!isExisting) {
    errors.push('Playbook is required for new users')
  }

  if (!isExisting && username) {
    if (!row.first_name) {
      errors.push('First name is required for new users')
    }
    if (!row.last_name) {
      errors.push('Last name is required for new users')
    }
  }

  const rowTags = collectRowTags(row, context.tagCategoryNames)
  const invalidRowTags = rowTags.filter((tag) => TAG_REGEX.test(tag))
  if (!isEmpty(invalidRowTags)) {
    errors.push(`Invalid tag(s): ${invalidRowTags.join(', ')}`)
  }

  Object.entries(FEATURE_GATED_PERMISSIONS).forEach(([permission, { flag, label }]) => {
    // Resolve the value from the canonical column or any legacy alias that
    // maps to the same permission, matching the backend's
    // `csv_user.get(canonical) or csv_user.get(alias)` precedence: the first
    // non-blank cell wins, so an explicit `no` in the canonical column beats a
    // `yes` in an alias.
    const aliasColumns = Object.entries(PERMISSION_COLUMN_ALIASES)
      .filter(([, target]) => target === permission)
      .map(([alias]) => alias)
    const effectiveValue = [permission, ...aliasColumns]
      .map((col) => row[col])
      .find((value) => value != null && String(value).trim() !== '')
    if (!isGrant(effectiveValue, permission)) {
      return
    }
    // Mirrors the backend transition guard: an org can sit without the screen
    // capture entitlement while users still have the per-user flag on, because
    // revoking the org entitlement does not clear it. Re-asserting the current
    // value is a no-op the server accepts, so flagging it here would be a false
    // positive on any CSV that round-trips existing values.
    if (permission === 'screen_capture_enabled' && existingUser?.screen_capture_enabled) {
      return
    }
    if (!context.organizationFeatureFlags[flag]) {
      errors.push(`Cannot grant ${label} — feature is not enabled for this organization`)
    }
    // Only enforced when complaints is being turned on, like the user form: a
    // row re-asserting it for a user who already has it shouldn't reject the
    // row's other edits. Only a missing voip_user_id column keeps an existing
    // user's `code`. The backend clears it on a blank cell, so a blank cell
    // can't fall back.
    const voipUserId = row.voip_user_id == null ? existingUser?.code : row.voip_user_id
    if (
      permission === 'newrez_complaints_enabled' &&
      !existingUser?.newrez_complaints_enabled &&
      !voipUserId?.trim()
    ) {
      errors.push(COMPLAINTS_REQUIRES_VOIP_USER_ID_ERROR)
    }
  })

  if (isExisting) {
    const statusLower = row.user_status?.toLowerCase()
    if (statusLower === 'deactivated' && !existingUser.deleted) {
      warnings.push('Will deactivate this user')
    } else if (statusLower === 'active' && existingUser.deleted) {
      warnings.push('Will restore this deactivated user')
    } else {
      warnings.push('Will update existing user')
    }
  }

  let status = STATUS_CLEAN
  if (!isEmpty(errors)) {
    status = STATUS_ERROR
  } else if (!isEmpty(warnings)) {
    status = STATUS_WARNING
  }

  return { status, errors, warnings }
}
